/**
 * APIKEY provider catalog — frontier-labs family (first-party frontier model labs).
 * Pure data; merged by apikey/index.ts via spread (god-file decomposition; semantic split).
 */
export const APIKEY_PROVIDERS_FRONTIER = {
  openai: {
    id: "openai",
    alias: "openai",
    name: "OpenAI",
    icon: "auto_awesome",
    color: "#10A37F",
    textIcon: "OA",
    website: "https://platform.openai.com",
  },
  reka: {
    id: "reka",
    alias: "reka",
    name: "Reka",
    icon: "auto_awesome",
    color: "#111827",
    textIcon: "RK",
    website: "https://docs.reka.ai/chat/overview",
    authHint:
      "Use your Reka API key. OmniRoute supports the OpenAI-compatible base URL https://api.reka.ai/v1 and sends both Authorization and X-Api-Key headers for compatibility.",
    apiHint:
      "Reka Chat is OpenAI-compatible on /v1. OmniRoute probes /v1/models and routes chat traffic to /v1/chat/completions.",
    hasFree: true,
    freeNote: "$10/month recurring free API credits",
  },
  pioneer: {
    id: "pioneer",
    alias: "pn",
    name: "Pioneer AI",
    icon: "rocket_launch",
    color: "#7C5CFF",
    textIcon: "PN",
    website: "https://pioneer.ai",
    notice: {
      text: "Pioneer AI by Fastino Labs. Free $75 usage credits, no credit card required. Use API key auth with a pio_sk_... key. Only open-tier models (Qwen3, Llama, Gemma, SmolLM) work directly — gated models (Claude/GPT/Gemini) require prior fine-tuning via the Pioneer platform.",
      apiKeyUrl: "https://agent.pioneer.ai/settings/api-keys",
      signupUrl: "https://agent.pioneer.ai/auth",
    },
    hasFree: true,
    freeNote: "$75 free usage credits — no credit card required",
    serviceKinds: ["llm"],
  },
  anthropic: {
    id: "anthropic",
    alias: "anthropic",
    name: "Anthropic",
    icon: "smart_toy",
    color: "#D97757",
    textIcon: "AN",
    website: "https://platform.claude.com",
  },
  gemini: {
    id: "gemini",
    alias: "gemini",
    name: "Gemini (Google AI Studio)",
    icon: "diamond",
    color: "#4285F4",
    textIcon: "GE",
    website: "https://aistudio.google.com",
    hasFree: true,
    freeNote:
      "Free forever: 1,500 req/day for Gemini 2.5 Flash — no credit card, get key at aistudio.google.com",
  },
  groq: {
    id: "groq",
    alias: "groq",
    name: "Groq",
    icon: "speed",
    color: "#F55036",
    textIcon: "GQ",
    website: "https://groq.com",
    hasFree: true,
    freeNote: "Free tier: 30 RPM / 14.4K RPD — no credit card",
  },
  blackbox: {
    id: "blackbox",
    alias: "bb",
    name: "Blackbox AI",
    icon: "view_in_ar",
    color: "#1A1A2E",
    textIcon: "BB",
    website: "https://blackbox.ai",
    hasFree: true,
    freeNote: "Free tier: unlimited basic chat plus Minimax-M2.5, no credit card required",
  },
  xai: {
    id: "xai",
    alias: "xai",
    name: "xAI (Grok)",
    icon: "auto_awesome",
    color: "#1DA1F2",
    textIcon: "XA",
    website: "https://x.ai",
  },
  mistral: {
    id: "mistral",
    alias: "mistral",
    name: "Mistral",
    icon: "air",
    color: "#FF7000",
    textIcon: "MI",
    website: "https://mistral.ai",
    hasFree: true,
    freeNote: "Free Experiment tier: rate-limited access to all models, no credit card required",
  },
  perplexity: {
    id: "perplexity",
    alias: "pplx",
    name: "Perplexity",
    icon: "search",
    color: "#20808D",
    textIcon: "PP",
    website: "https://www.perplexity.ai",
  },
  cohere: {
    id: "cohere",
    alias: "cohere",
    name: "Cohere",
    icon: "hub",
    color: "#39594D",
    textIcon: "CO",
    website: "https://cohere.com",
    hasFree: true,
    freeNote: "Free Trial: 1,000 API calls/month for testing, no credit card required",
  },
  "meta-llama": {
    id: "meta-llama",
    alias: "meta",
    name: "Meta Llama API",
    icon: "smart_toy",
    color: "#0F766E",
    textIcon: "ML",
    website: "https://llama.developer.meta.com",
  },
  morph: {
    id: "morph",
    alias: "morph",
    name: "Morph",
    icon: "auto_fix_high",
    color: "#2563EB",
    textIcon: "MP",
    website: "https://morphllm.com",
    hasFree: true,
    freeNote: "Free tier: 250K credits/month, $0",
  },
  galadriel: {
    id: "galadriel",
    alias: "galadriel",
    name: "Galadriel",
    icon: "auto_awesome",
    color: "#F59E0B",
    textIcon: "GA",
    website: "https://galadriel.com",
    subscriptionRisk: true,
    riskNoticeVariant: "deprecated",
    deprecated: true,
    deprecationReason:
      "api.galadriel.ai no longer resolves (sweep 2026-06-19); the inference API appears discontinued.",
  },
  ai21: {
    id: "ai21",
    alias: "ai21",
    name: "AI21 Labs",
    icon: "psychology_alt",
    color: "#0284C7",
    textIcon: "AI21",
    website: "https://www.ai21.com",
    hasFree: true,
    freeNote: "$10 trial credits on signup (valid 3 months), no credit card required",
  },
  venice: {
    id: "venice",
    alias: "venice",
    name: "Venice.ai",
    icon: "travel_explore",
    color: "#0EA5E9",
    textIcon: "VN",
    website: "https://venice.ai",
  },
  codestral: {
    id: "codestral",
    alias: "codestral",
    name: "Codestral",
    icon: "terminal",
    color: "#FF7000",
    textIcon: "CS",
    website: "https://mistral.ai",
  },
  upstage: {
    id: "upstage",
    alias: "upstage",
    name: "Upstage",
    icon: "trending_up",
    color: "#0F766E",
    textIcon: "UP",
    website: "https://www.upstage.ai",
  },
  maritalk: {
    id: "maritalk",
    alias: "maritalk",
    name: "Maritalk",
    icon: "translate",
    color: "#1D4ED8",
    textIcon: "MT",
    website: "https://www.maritaca.ai",
  },
  "nous-research": {
    id: "nous-research",
    alias: "nous",
    name: "Nous Research",
    icon: "hub",
    color: "#2563EB",
    textIcon: "NO",
    website: "https://portal.nousresearch.com/help",
    authHint:
      "Use your Nous Portal API key. OmniRoute targets the official OpenAI-compatible inference endpoint at https://inference-api.nousresearch.com/v1.",
    apiHint:
      "Nous exposes an OpenAI-compatible /v1 surface with a large remote /models catalog. The /chat/completions endpoint requires a valid API key for programmatic inference.",
    hasFree: true,
    freeNote: "Free tier: 50 RPM, 500,000 TPM — no credit card",
  },
  "arcee-ai": {
    id: "arcee-ai",
    alias: "arcee",
    name: "Arcee AI",
    icon: "auto_awesome",
    color: "#8B5CF6",
    textIcon: "AR",
    website: "https://arcee.ai",
    hasFree: true,
    freeNote: "Free Trinity Large Thinking model (262K context). No credit card required.",
    passthroughModels: true,
    authHint: "Get API key at arcee.ai",
  },
  liquid: {
    id: "liquid",
    alias: "liquid",
    name: "Liquid AI",
    icon: "water_drop",
    color: "#06B6D4",
    textIcon: "LQ",
    website: "https://liquid.ai",
    hasFree: true,
    freeNote:
      "Free LFM2.5-1.2B-Thinking and LFM2.5-1.2B-Instruct models. MIT spinoff, hybrid architecture.",
    passthroughModels: true,
    authHint: "Get API key at liquid.ai",
  },
};
